import { useRef, useState } from "react";

import {
  applyCatalogImport,
  CatalogImportApiError,
  previewCatalogImport,
} from "./api";
import { navigate } from "./navigation";
import type { CatalogImportIssue, CatalogImportPreview } from "./types";

const MAX_FILE_SIZE = 1_000_000;

function Count({ label, value }: { label: string; value: { create: number; update: number; unchanged: number } }) {
  return (
    <div className="import-count">
      <span>{label}</span>
      <strong>{value.create + value.update + value.unchanged}</strong>
      <small>
        {value.create > 0 && `${value.create} à créer`}
        {value.update > 0 && `${value.update} à mettre à jour`}
        {value.unchanged > 0 && `${value.unchanged} inchangé`}
      </small>
    </div>
  );
}

function Issues({ title, issues }: { title: string; issues: CatalogImportIssue[] }) {
  if (!issues.length) return null;
  return (
    <section className={`import-issues ${title === "Erreurs" ? "errors" : "warnings"}`}>
      <strong>{title}</strong>
      <ul>
        {issues.map((issue) => <li key={`${issue.code}-${issue.path}`}><code>{issue.path}</code> — {issue.message}</li>)}
      </ul>
    </section>
  );
}

export function CatalogImportPage() {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CatalogImportPreview | null>(null);
  const [issues, setIssues] = useState<CatalogImportIssue[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const chooseFile = (next: File | null) => {
    setPreview(null);
    setIssues([]);
    setDone(false);
    if (!next) { setFile(null); return; }
    if (!next.name.toLowerCase().endsWith(".json") && !next.name.toLowerCase().endsWith(".csv")) {
      setFile(null); setMessage("Choisissez un fichier CSV ou JSON."); return;
    }
    if (next.size > MAX_FILE_SIZE) { setFile(null); setMessage("Le fichier ne doit pas dépasser 1 Mo."); return; }
    setMessage(null);
    setFile(next);
  };

  const previewFile = async () => {
    if (!file) return;
    setLoading(true); setMessage(null); setIssues([]);
    try {
      setPreview(await previewCatalogImport(file));
    } catch (reason) {
      if (reason instanceof CatalogImportApiError) {
        setIssues(reason.issues);
        setMessage(reason.issues.length ? "Le catalogue doit être corrigé avant import." : reason.message);
      } else setMessage("La prévisualisation est indisponible.");
    } finally { setLoading(false); }
  };

  const apply = async () => {
    if (!file || !preview) return;
    setLoading(true); setMessage(null);
    try {
      const result = await applyCatalogImport(file, preview.fingerprint);
      setPreview(result); setDone(true);
      setMessage(result.idempotent ? "Ce catalogue était déjà intégré : aucune donnée n’a été dupliquée." : "Catalogue intégré avec succès. Vous pouvez retrouver les séries dans votre bibliothèque.");
    } catch (reason) {
      if (reason instanceof CatalogImportApiError) { setIssues(reason.issues); setMessage(reason.message); }
      else setMessage("L’import n’a pas pu être finalisé.");
    } finally { setLoading(false); }
  };

  return (
    <section className="catalog-import-page">
      <button className="back-button" type="button" onClick={() => navigate("/mes-series")}>← Retour à mes séries</button>
      <header className="catalog-import-heading">
        <p className="eyebrow">Bibliothèque pédagogique</p>
        <h1>Ajouter un catalogue</h1>
        <p>Importez un CSV ou JSON, vérifiez son contenu, puis confirmez. Rien n’est ajouté avant votre validation.</p>
      </header>
      <section className="import-stepper" aria-label="Étapes de l’import">
        <span className={file ? "active" : ""}>1. Fichier</span><span className={preview ? "active" : ""}>2. Vérification</span><span className={done ? "active" : ""}>3. Intégration</span>
      </section>
      <section className="import-dropzone">
        <input ref={input} type="file" accept=".csv,.json,text/csv,application/json" onChange={(event) => chooseFile(event.target.files?.[0] ?? null)} />
        <span aria-hidden="true">⇪</span>
        <h2>{file ? file.name : "Choisir un catalogue"}</h2>
        <p>{file ? `${Math.ceil(file.size / 1024)} Ko · prêt à vérifier` : "Formats CSV ou JSON · 1 Mo maximum"}</p>
        <button className="button" type="button" onClick={() => input.current?.click()}>Parcourir mes fichiers</button>
      </section>
      {message && <div className={done ? "import-success" : "info-notice"} role="status">{message}</div>}
      <Issues title="Erreurs" issues={issues} />
      {file && !preview && <button className="button button-primary import-main-action" type="button" disabled={loading} onClick={() => void previewFile()}>{loading ? "Vérification…" : "Vérifier le catalogue"}</button>}
      {preview && <>
        <section className="import-preview">
          <div><p className="eyebrow">Prévisualisation</p><h2>{preview.catalog.external_id}</h2><p>Version {preview.catalog.version} · aucune écriture n’a encore été réalisée.</p></div>
          <div className="import-counts"><Count label="Séries" value={preview.summary.collections} /><Count label="Cartes" value={preview.summary.cards} /><Count label="Faces" value={preview.summary.faces} /></div>
          <div className="import-collection-list">{preview.collections.map((collection) => <p key={collection.external_id}><strong>{collection.serial_number}</strong><span>{collection.cards.length} cartes · {collection.cards.reduce((total, card) => total + card.faces, 0)} faces</span></p>)}</div>
        </section>
        <Issues title="À noter" issues={preview.warnings} />
        {!done && <div className="import-confirm"><div><strong>Prêt à intégrer</strong><p>Le serveur revérifiera le fichier et son empreinte avant toute écriture.</p></div><button className="button button-primary" type="button" disabled={loading} onClick={() => void apply()}>{loading ? "Intégration…" : "Confirmer l’import"}</button></div>}
        {done && <button className="button button-primary" type="button" onClick={() => navigate("/mes-series")}>Voir mes séries</button>}
      </>}
    </section>
  );
}
